import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DatePicker }           from '@mui/x-date-pickers'
import { AdapterMoment }        from '@mui/x-date-pickers/AdapterMoment'
import { LocalizationProvider } from '@mui/x-date-pickers'
import moment                   from 'moment'
import { useDispatch, useSelector } from 'react-redux'
import { shallowEqual } from 'react-redux'
import {
  Alert, Box, Button, Checkbox, ClickAwayListener, Divider, FormControl,
  InputAdornment, MenuItem, Paper, Popper, Snackbar,
  Select, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material'
import SearchIcon      from '@mui/icons-material/Search'
import SettingsIcon    from '@mui/icons-material/Settings'
import VisibilityIcon    from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import {
  selectBeacon4Filters,
  selectBeacon4AllJvids,
  selectBeacon4Module,
  selectBeacon4PerfTech,
  selectBeacon4PerfMainKpi,
  selectBeacon4PerfFiltersRemoved,
  selectBeacon4LegendVisible,
  setBeacon4AdminDialog,
  setBeacon4Aggregation, setBeacon4Side,
  setBeacon4Polygon, setBeacon4Jvid,
  setBeacon4PerfSide, setBeacon4PerfPolygon, setBeacon4PerfVendor, setBeacon4PerfJvid,
  clearBeacon4PerfFiltersRemoved,
  toggleBeacon4Legend,
  setBeacon4DateFrom, setBeacon4DateTo,
  setBeacon4PerfTech, setBeacon4PerfMainKpi,
  setBeacon4Module,
  loadBeacon4SidePolygons,
  loadBeacon4PerformanceFilters,
} from '../../reducers/Beacon4FiltersReducer'
import { reloadBeacon4View }            from '../../actions/Beacon4PerformanceActions'
import { reloadBeacon4PerformanceView } from '../../actions/Beacon4PerformanceModuleActions'
import {
  selectBeacon4PerformanceAvailableTechs,
  selectBeacon4PerformanceAvailableKpisByTech,
} from '../../reducers/Beacon4PerformanceKpiDataReducer'

const AGG_OPTIONS = ['Daily', 'Weekly', 'Monthly']

function Label({ children }) {
  return (
    <Typography sx={{ fontSize: 9, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em', mb: '3px' }}>
      {children}
    </Typography>
  )
}

// ── MultiSelectDropdown ──────────────────────────────────────────────────────

// Virtualised list — only renders visible rows (fixes performance with many JV IDs)
const ITEM_H = 28
const MAX_VISIBLE = 8

const VirtualList = memo(function VirtualList({ items, selectedSet, onToggle }) {
  const [scrollTop, setScrollTop] = useState(0)
  const containerH = Math.min(items.length, MAX_VISIBLE) * ITEM_H

  if (items.length === 0) {
    return <MenuItem disabled sx={{ fontSize: 11 }}>No results</MenuItem>
  }

  const startIdx   = Math.floor(scrollTop / ITEM_H)
  const endIdx     = Math.min(startIdx + MAX_VISIBLE + 1, items.length)
  const visible    = items.slice(startIdx, endIdx)
  const paddingTop = startIdx * ITEM_H

  return (
    <Box
      onScroll={e => setScrollTop(e.currentTarget.scrollTop)}
      sx={{ height: containerH, overflowY: 'auto', position: 'relative', py: 0.5 }}
    >
      <Box sx={{ height: items.length * ITEM_H, position: 'relative' }}>
        <Box sx={{ position: 'absolute', top: paddingTop, left: 0, right: 0 }}>
          {visible.map(opt => (
            <Box key={opt} onClick={() => onToggle(opt)}
              sx={{ height: ITEM_H, display: 'flex', alignItems: 'center', px: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
              <Checkbox checked={selectedSet.has(opt)} size="small" sx={{ p: 0.5 }} disableRipple />
              <Typography sx={{ fontSize: 11 }}>{opt}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  )
})

const MultiSelectDropdown = memo(function MultiSelectDropdown({ label, value = '', options = [], onChange, disabled }) {
  const [open, setOpen]     = useState(false)
  const [search, setSearch] = useState('')
  const anchorRef           = useRef(null)
  const onChangeRef         = useRef(onChange)
  useEffect(() => { onChangeRef.current = onChange }, [onChange])

  const selectedSet = useMemo(() => new Set(typeof value === 'string' ? value.split(',').filter(Boolean) : value), [value])
  const filtered    = useMemo(() => options.filter(o => o.toLowerCase().includes(search.toLowerCase())), [options, search])

  const toggle          = useCallback((opt) => {
    const next = new Set(selectedSet)
    next.has(opt) ? next.delete(opt) : next.add(opt)
    onChangeRef.current([...next].join(','))
  }, [selectedSet])

  const handleSelectAll = useCallback(() => { onChangeRef.current(filtered.join(',')) }, [filtered])
  const handleClear     = useCallback(() => { onChangeRef.current('') }, [])
  const handleClose     = useCallback(() => { setOpen(false); setSearch('') }, [])
  const selected        = useMemo(() => [...selectedSet], [selectedSet])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Label>{label}</Label>
      <Box ref={anchorRef}>
        <Button
          variant="outlined" size="small" disabled={disabled}
          onClick={() => !disabled && setOpen(v => !v)}
          sx={{
            height: 28, fontSize: 11, minWidth: 130, maxWidth: 200,
            textTransform: 'none', justifyContent: 'space-between',
            color: selected.length ? 'text.primary' : 'text.disabled',
            borderColor: 'divider', bgcolor: 'background.default',
            '&:hover': { borderColor: 'primary.main' },
            overflow: 'hidden',
          }}
          endIcon={<Typography sx={{ fontSize: 10, ml: 0.5 }}>▾</Typography>}
        >
          <Typography noWrap sx={{ fontSize: 11, maxWidth: 150 }}>
            {selected.length === 0 ? '(All)' : selected.length === 1 ? selected[0] : `${selected.length} selected`}
          </Typography>
        </Button>
      </Box>

      <Popper open={open} anchorEl={anchorRef.current} placement="bottom-start" sx={{ zIndex: 1400 }}>
        <ClickAwayListener onClickAway={handleClose}>
          <Paper elevation={4} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden', minWidth: 200, maxWidth: 260 }}>
            <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
              <TextField
                size="small" fullWidth autoFocus placeholder="Search..."
                value={search} onChange={e => setSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 14, color: 'text.disabled' }} /></InputAdornment> }}
                sx={{ '& .MuiOutlinedInput-root': { height: 26, fontSize: 11 } }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, px: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Button size="small" sx={{ fontSize: 10, textTransform: 'none', py: 0 }} onClick={handleClear}>Clear</Button>
              <Button size="small" sx={{ fontSize: 10, textTransform: 'none', py: 0 }} onClick={handleSelectAll}>Select all</Button>
            </Box>
            <VirtualList items={filtered} selectedSet={selectedSet} onToggle={toggle} />
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  )
})

// ── FilterSelect ─────────────────────────────────────────────────────────────
function FilterSelect({ label, value, options = [], onChange, disabled }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {label && <Label>{label}</Label>}
      <FormControl size="small" disabled={disabled}>
        <Select
          value={value ?? ''} onChange={e => onChange(e.target.value)} displayEmpty
          sx={{ height: 28, fontSize: 11, minWidth: 110, bgcolor: 'background.default', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' } }}
        >
          <MenuItem value="" sx={{ fontSize: 11 }}>(All)</MenuItem>
          {options.map(o => <MenuItem key={o} value={o} sx={{ fontSize: 11 }}>{o}</MenuItem>)}
        </Select>
      </FormControl>
    </Box>
  )
}

// ── FilterBar ────────────────────────────────────────────────────────────────
// Visual group wrapper — adds a subtle "title" above a horizontal row of fields
function FieldGroup({ title, children }) {
  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', gap: 0.25,
      border: '1px solid', borderColor: 'divider',
      borderRadius: 1, px: 1, py: 0.5,
      bgcolor: 'rgba(0,0,0,0.015)',
    }}>
      <Typography sx={{
        fontSize: 8.5, fontWeight: 700, color: 'text.disabled',
        textTransform: 'uppercase', letterSpacing: '0.08em',
        lineHeight: 1, mb: -0.25,
      }}>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
        {children}
      </Box>
    </Box>
  )
}


export default function Beacon4FilterBar({ exportButton, externalRole }) {
  const dispatch      = useDispatch()
  const {
    aggregation, dateFrom, dateTo, side, polygon, jvid, sides, polygons, jvids,
    perfSide, perfPolygon, perfVendor, perfJvid, perfSides, perfPolygons, perfVendors, perfJvids,
  } = useSelector(selectBeacon4Filters, shallowEqual)
  const allJvids      = useSelector(selectBeacon4AllJvids)
  const perfFiltersRemoved = useSelector(selectBeacon4PerfFiltersRemoved)
  const legendVisible = useSelector(selectBeacon4LegendVisible)
  const module        = useSelector(selectBeacon4Module)
  const perfTech      = useSelector(selectBeacon4PerfTech)
  const perfMainKpi   = useSelector(selectBeacon4PerfMainKpi)
  const canAdmin      = externalRole === 'Administrator' || externalRole === 'Manager'
  const perfTechs        = useSelector(selectBeacon4PerformanceAvailableTechs)
  const perfKpisByTech   = useSelector(selectBeacon4PerformanceAvailableKpisByTech)
  const jvidOptions   = side ? jvids : allJvids
  const canApply = !!(dateFrom && dateTo && dateFrom <= dateTo) && (module !== 'Performance' || !!perfMainKpi)

  useEffect(() => { dispatch(loadBeacon4SidePolygons()) }, []) // eslint-disable-line

  // All KPI keys (Main + Breakdown) offered for a given Tech — used to decide
  // whether a Tech change should keep the current Main/Table KPI (and by
  // extension the filter selections under them) or reset them, instead of
  // always resetting outright.
  const kpiKeysForTech = useCallback(
    (t) => (perfKpisByTech?.[t] ?? []).map(k => k.key),
    [perfKpisByTech]
  )

  // Auto-correct perfTech if it isn't in the available techs (e.g. DB only has 3G+).
  // Fires whenever the available list changes.
  useEffect(() => {
    if (!perfTechs?.length) return
    if (!perfTechs.includes(perfTech)) {
      const nextTech = perfTechs[0]
      dispatch(setBeacon4PerfTech({ tech: nextTech, availableKeys: kpiKeysForTech(nextTech) }))
    }
  }, [dispatch, perfTechs, perfTech, kpiKeysForTech])

  // KPI Selection is the first point of contact for Performance: once both
  // Tech and Main KPI are chosen, fetch the Side/Polygon/Vendor/JV ID options
  // scoped to that KPI's source view and the current date range. Re-fires on
  // Tech/KPI change (may resolve to a different view → reducer resets the
  // current selections) and on date range change too (Last_Date is filtered
  // by that range — same view usually → reducer keeps the current selections).
  useEffect(() => {
    if (module !== 'Performance') return
    if (!perfTech || !perfMainKpi) return
    if (!dateFrom || !dateTo) return
    dispatch(loadBeacon4PerformanceFilters({ tech: perfTech, mainKpiKey: perfMainKpi, dateFrom, dateTo }))
  }, [dispatch, module, perfTech, perfMainKpi, dateFrom, dateTo])

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Paper elevation={0} square sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, px: 2.5, pt: 1, pb: 0.75 }}>

          {/* ── ROW 1: Module · Date · Filters · Aggregation · Apply ───────────────────── */}
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, flexWrap: 'wrap' }}>

            {/* GROUP: Module switch (was a standalone bar above the FilterBar) */}
            <FieldGroup title="Module">
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Label>&nbsp;</Label>
                <ToggleButtonGroup exclusive value={module} size="small"
                  onChange={(_, v) => { if (v) dispatch(setBeacon4Module(v)) }}
                  sx={{ height: 28 }}>
                  {['Blended', 'Performance'].map(m => (
                    <ToggleButton key={m} value={m} sx={{
                      px: 1.75, fontSize: 11, fontWeight: 500, textTransform: 'none',
                      '&.Mui-selected': { bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' } },
                    }}>{m}</ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
            </FieldGroup>

            {/* GROUP: Date range */}
            <FieldGroup title="Date">
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Label>First Day</Label>
                <DatePicker
                  format="YYYY-MM-DD"
                  value={dateFrom ? moment(dateFrom, 'YYYY-MM-DD') : null}
                  onChange={v => dispatch(setBeacon4DateFrom(v?.isValid() ? v.format('YYYY-MM-DD') : ''))}
                  maxDate={dateTo ? moment(dateTo, 'YYYY-MM-DD').subtract(1, 'day') : undefined}
                  slotProps={{ textField: { size: 'small', sx: { width: 138, '& .MuiOutlinedInput-root': { height: 28, fontSize: 11 } } } }}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Label>Last Day</Label>
                <DatePicker
                  format="YYYY-MM-DD"
                  value={dateTo ? moment(dateTo, 'YYYY-MM-DD') : null}
                  onChange={v => dispatch(setBeacon4DateTo(v?.isValid() ? v.format('YYYY-MM-DD') : ''))}
                  minDate={dateFrom ? moment(dateFrom, 'YYYY-MM-DD').add(1, 'day') : undefined}
                  slotProps={{ textField: { size: 'small', sx: { width: 138, '& .MuiOutlinedInput-root': { height: 28, fontSize: 11 } } } }}
                />
              </Box>
            </FieldGroup>

            {/* GROUP: Options — sits right next to Date, in both modules.
                Order is Legend, Export, Manage KPIs (Performance + admin
                only). Legend/Export apply regardless of module; Manage KPIs
                is the only module-specific entry. */}
            <FieldGroup title="Options">
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Label>&nbsp;</Label>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Tooltip title={legendVisible ? 'Hide legend' : 'Show legend'}>
                    <ToggleButton value="legend" selected={legendVisible} size="small"
                      onChange={() => dispatch(toggleBeacon4Legend())}
                      sx={{
                        height: 28, px: 1.25, fontSize: 11, textTransform: 'none', gap: 0.5,
                        '&.Mui-selected': { bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' } },
                      }}>
                      {legendVisible ? <VisibilityIcon sx={{ fontSize: 15 }} /> : <VisibilityOffIcon sx={{ fontSize: 15 }} />}
                      Legend
                    </ToggleButton>
                  </Tooltip>
                  {exportButton}
                  {module === 'Performance' && canAdmin && (
                    <Tooltip title="Manage KPIs — definitions, formulas, breakdowns and audit">
                      <Button variant="outlined" size="small" startIcon={<SettingsIcon sx={{ fontSize: 16 }} />}
                        onClick={() => dispatch(setBeacon4AdminDialog(true))}
                        sx={{ height: 28, px: 1.5, fontSize: 11, textTransform: 'none', whiteSpace: 'nowrap' }}>
                        Manage KPIs
                      </Button>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </FieldGroup>

            {/* GROUP: Filters — Blended only here (unscoped, global Side/
                Polygon/JV ID list). Performance now has its OWN Filters
                group on ROW 2, right after KPI Selection, scoped to the
                selected KPI's source view + date range (see below). */}
            {module !== 'Performance' && (
              <FieldGroup title="Filters">
                <MultiSelectDropdown label="Side"    value={side}    options={sides}       onChange={v => dispatch(setBeacon4Side(v))} />
                <MultiSelectDropdown label="Polygon" value={polygon} options={polygons}    onChange={v => dispatch(setBeacon4Polygon(v))} disabled={polygons.length === 0} />
                <MultiSelectDropdown label="JV ID"   value={jvid}    options={jvidOptions} onChange={v => dispatch(setBeacon4Jvid(v))} />
              </FieldGroup>
            )}

            {/* GROUP: Time Aggregation — Blended only here. For Performance,
                Time moves down to ROW 2, next to KPI Selection (see below),
                since that's where the KPI picker actually lives. A dropdown
                instead of a 3-button toggle group — same choice either way,
                just narrower, which matters for keeping this whole row on a
                single line. (Not FilterSelect — that always adds an "(All)"
                option, which doesn't make sense here: aggregation always has
                a definite value, never "none".) */}
            {module !== 'Performance' && (
              <FieldGroup title="Time">
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Label>Aggregation</Label>
                  <FormControl size="small">
                    <Select
                      value={aggregation}
                      onChange={e => dispatch(setBeacon4Aggregation(e.target.value))}
                      sx={{ height: 28, fontSize: 11, minWidth: 100, bgcolor: 'background.default',
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' } }}>
                      {AGG_OPTIONS.map(a => <MenuItem key={a} value={a} sx={{ fontSize: 11 }}>{a}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Box>
              </FieldGroup>
            )}

            {/* Apply — Blended only here; pinned to the far right by the
                spacer. Performance's Apply is on ROW 2. */}
            <Box sx={{ flex: 1 }} />
            {module !== 'Performance' && (
              <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                <Button variant="contained" size="small"
                  onClick={() => dispatch(reloadBeacon4View())}
                  disabled={!canApply}
                  sx={{ height: 30, px: 3, fontWeight: 600 }}>
                  Apply
                </Button>
              </Box>
            )}

          </Box>

          {/* ── ROW 2: Performance-only KPI selection + Apply/Export ──────────────────── */}
          {module === 'Performance' && (
            <Box sx={{
              display: 'flex', alignItems: 'flex-end', gap: 1.5, flexWrap: 'wrap',
              pt: 0.75, borderTop: '1px dashed', borderColor: 'divider',
            }}>
              <FieldGroup title="KPI Selection">
                {/* Tech */}
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Label>Tech</Label>
                  <ToggleButtonGroup exclusive value={perfTech} size="small"
                    onChange={(_, v) => { if (v) dispatch(setBeacon4PerfTech({ tech: v, availableKeys: kpiKeysForTech(v) })) }}
                    sx={{ height: 28 }}>
                    {(perfTechs ?? []).map(t => (
                      <ToggleButton key={t} value={t} sx={{
                        px: 1.25, fontSize: 11, fontWeight: 500, textTransform: 'none',
                        '&.Mui-selected': { bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' } },
                      }}>{t}</ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>
                {/* KPI */}
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Label>KPI *</Label>
                  <FormControl size="small">
                    <Select
                      value={perfMainKpi || ''}
                      onChange={e => dispatch(setBeacon4PerfMainKpi(e.target.value))}
                      displayEmpty
                      sx={{ height: 28, fontSize: 11, minWidth: 220, bgcolor: 'background.default',
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' } }}>
                      <MenuItem value="" sx={{ fontSize: 11, color: 'text.disabled' }}>(Select KPI)</MenuItem>
                      {/* Backend now returns ALL active KPIs (Main + Breakdowns).
                          This dropdown drives the dashboard, so it must show
                          only Main KPIs — filter by category here. */}
                      {(perfKpisByTech?.[perfTech] ?? [])
                        .filter(k => k.category === 'Main KPI')
                        .map(k => (
                          <MenuItem key={k.key} value={k.key} sx={{ fontSize: 11 }}>{k.label}</MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Box>
              </FieldGroup>

              {/* GROUP: Filters — Performance's own Side/Polygon/Vendor/JV ID,
                  scoped to the KPI just picked above (its source view) and the
                  current date range. Sits right after KPI Selection since it
                  literally can't resolve to anything before a KPI is chosen —
                  disabled/empty until then. */}
              <FieldGroup title="Filters">
                <MultiSelectDropdown label="Side" value={perfSide} options={perfSides}
                  onChange={v => dispatch(setBeacon4PerfSide(v))} disabled={!perfMainKpi} />
                <MultiSelectDropdown label="Polygon" value={perfPolygon} options={perfPolygons}
                  onChange={v => dispatch(setBeacon4PerfPolygon(v))} disabled={!perfMainKpi || perfPolygons.length === 0} />
                <MultiSelectDropdown label="Vendor" value={perfVendor} options={perfVendors}
                  onChange={v => dispatch(setBeacon4PerfVendor(v))} disabled={!perfMainKpi || perfVendors.length === 0} />
                <MultiSelectDropdown label="JV ID" value={perfJvid} options={perfJvids}
                  onChange={v => dispatch(setBeacon4PerfJvid(v))} disabled={!perfMainKpi} />
              </FieldGroup>

              {/* GROUP: Time Aggregation — lives here (not ROW 1) for
                  Performance, right next to the KPI picker it applies to.
                  Dropdown rather than a toggle group, same as Blended's
                  Row 1 version — narrower, so this row wraps less. */}
              <FieldGroup title="Time">
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Label>Aggregation</Label>
                  <FormControl size="small">
                    <Select
                      value={aggregation}
                      onChange={e => dispatch(setBeacon4Aggregation(e.target.value))}
                      sx={{ height: 28, fontSize: 11, minWidth: 100, bgcolor: 'background.default',
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' } }}>
                      {AGG_OPTIONS.map(a => <MenuItem key={a} value={a} sx={{ fontSize: 11 }}>{a}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Box>
              </FieldGroup>

              {/* Spacer pushes Apply to the true right edge of this row —
                  Export moved up to ROW 1's Options group, so Apply is the
                  only thing left here. Apply only fires once a KPI is chosen. */}
              <Box sx={{ flex: 1 }} />
              <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                <Button variant="contained" size="small"
                  onClick={() => dispatch(reloadBeacon4PerformanceView())}
                  disabled={!canApply}
                  sx={{ height: 30, px: 3, fontWeight: 600 }}>
                  Apply
                </Button>
              </Box>
            </Box>
          )}

        </Box>
      </Paper>

      {/* Notice for filters dropped on the last refetch (KPI/date range
          resolved to a source view where a previously-picked Side/Polygon/
          Vendor/JV ID no longer exists). Anything that's still valid is kept
          silently — this only fires for the part that genuinely couldn't be
          carried over, so it doesn't nag the user on every single refetch. */}
      <Snackbar
        open={module === 'Performance' && perfFiltersRemoved.length > 0}
        autoHideDuration={8000}
        onClose={() => dispatch(clearBeacon4PerfFiltersRemoved())}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          severity="info" variant="filled"
          onClose={() => dispatch(clearBeacon4PerfFiltersRemoved())}
          sx={{ fontSize: 12 }}
        >
          {perfFiltersRemoved.map(r => `${r.field}: ${r.values.join(', ')}`).join(' · ')} — not available for this KPI/date range, removed.
        </Alert>
      </Snackbar>
    </LocalizationProvider>
  )
}